import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import Loading from '../Components/Loading';

import { API_URL } from '../config/api';
import {
    broadcastEmployeeForceLogout,
    performLogout,
    shouldForceLogoutCurrentUser
} from '../utils/auth';

const normalizeAssetsList = (data) => {
    if (!Array.isArray(data)) return [];
    return data.filter((item) => item && item.id && item.assetName);
};

const getAssetName = (asset) => asset?.assetName || asset?.name || '-';
const getDescription = (asset) => asset?.Description ?? asset?.description ?? '-';

const getStatusClass = (status) => {
    if (!status || status === '-') return 'active';
    return String(status).toLowerCase().replace(/\s+/g, '-');
};

const AssetsHandover = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const initialEmployeeId = location.state?.employeeId;

    const [resolvedEmployeeId, setResolvedEmployeeId] = useState(initialEmployeeId || '');
    const [employeeName, setEmployeeName] = useState(location.state?.employeeName || '-');
    const [employeeCode, setEmployeeCode] = useState(location.state?.employeeCode || '-');
    const [assets, setAssets] = useState([]);
    const [selectedAssetIds, setSelectedAssetIds] = useState([]);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [pagination, setPagination] = useState({
        currentPage: 1,
        totalPages: 1,
        totalCount: 0,
        limit: 10,
        hasNextPage: false,
        hasPreviousPage: false
    });

    useEffect(() => {
        if (!initialEmployeeId) {
            toast.error('Employee identifier is missing.');
            navigate('/application-status');
            return;
        }
        fetchAssets();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialEmployeeId, pagination.currentPage, pagination.limit]);

    const fetchAssets = async () => {
        if (!API_URL) {
            toast.error('API URL is not configured. Please set REACT_APP_API_URL in environment variables.');
            return;
        }

        setLoading(true);
        try {
            const payload = {
                action: 'get-assets-by-employee',
                employeeId: initialEmployeeId,
                page: pagination.currentPage,
                limit: pagination.limit
            };

            const response = await axios.post(API_URL, payload, {
                headers: { 'Content-Type': 'application/json' }
            });

            const result = response.data;

            if (result?.success) {
                const list = normalizeAssetsList(result.data || result.assets || []);
                setAssets(list);
                if (result.employeeId) {
                    setResolvedEmployeeId(result.employeeId);
                }
                if (result.employeeName) {
                    setEmployeeName(result.employeeName);
                }
                const currentPage = result.page || result.pagination?.currentPage || pagination.currentPage;
                const totalPages = result.totalPages || result.pagination?.totalPages || 1;
                setPagination(prev => ({
                    ...prev,
                    currentPage,
                    totalPages,
                    totalCount: result.total ?? result.pagination?.total ?? prev.totalCount,
                    hasNextPage: result.pagination?.hasNextPage ?? currentPage < totalPages,
                    hasPreviousPage: result.pagination?.hasPreviousPage ?? currentPage > 1
                }));
                setSelectedAssetIds((prev) =>
                    prev.filter((id) => list.some((asset) => asset.id === id))
                );
            } else {
                toast.error(result?.message || 'Failed to fetch employee assets');
            }
        } catch (error) {
            console.error('Error fetching employee assets:', error);
            const errorMessage = error.response?.data?.message || error.message || 'Something went wrong. Please try again.';
            toast.error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return dateString;
            const day = date.getDate();
            const month = date.toLocaleString('default', { month: 'short' });
            const year = date.getFullYear();
            return `${day}-${month}-${year}`;
        } catch (error) {
            return dateString;
        }
    };

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= pagination.totalPages) {
            setPagination(prev => ({ ...prev, currentPage: newPage }));
        }
    };

    const handleLimitChange = (e) => {
        const newLimit = parseInt(e.target.value, 10);
        setPagination(prev => ({ ...prev, limit: newLimit, currentPage: 1 }));
    };

    const getSerialNumber = (index) => {
        return (pagination.currentPage - 1) * pagination.limit + index + 1;
    };

    const getAssetStatus = (asset) => asset?.status || asset?.assetStatus || '-';

    const isAssetSelected = (assetId) => selectedAssetIds.includes(assetId);

    const allCurrentPageSelected =
        assets.length > 0 && assets.every((asset) => isAssetSelected(asset.id));

    const handleToggleAsset = (assetId) => {
        setSelectedAssetIds((prev) =>
            prev.includes(assetId)
                ? prev.filter((id) => id !== assetId)
                : [...prev, assetId]
        );
    };

    const handleSelectAll = (e) => {
        const checked = e.target.checked;
        const pageIds = assets.map((asset) => asset.id);
        if (checked) {
            setSelectedAssetIds((prev) => [...new Set([...prev, ...pageIds])]);
        } else {
            setSelectedAssetIds((prev) => prev.filter((id) => !pageIds.includes(id)));
        }
    };

    const handleSubmitHandover = async () => {
        const employeeIdForSubmit = resolvedEmployeeId || initialEmployeeId;

        if (!employeeIdForSubmit) {
            toast.error('Employee identifier is missing.');
            return;
        }

        if (selectedAssetIds.length === 0) {
            toast.error('Please select at least one asset to hand over.');
            return;
        }

        if (!API_URL) {
            toast.error('API URL is not configured.');
            return;
        }

        setSubmitting(true);
        try {
            const payload = {
                action: 'unallocate-assets-complete-resignation',
                employeeId: employeeIdForSubmit,
                assetIds: selectedAssetIds
            };

            const response = await axios.post(API_URL, payload, {
                headers: { 'Content-Type': 'application/json' }
            });

            const result = response.data;

            if (result?.success) {
                toast.success(
                    result?.message ||
                        'Assets handover completed. The employee will be signed out on all devices shortly.'
                );
                setSelectedAssetIds([]);
                broadcastEmployeeForceLogout(employeeIdForSubmit);

                if (shouldForceLogoutCurrentUser(employeeIdForSubmit)) {
                    performLogout(navigate);
                } else {
                    navigate('/application-status');
                }
            } else {
                toast.error(result?.message || 'Failed to complete assets handover');
            }
        } catch (error) {
            console.error('Error completing assets handover:', error);
            const errorMessage = error.response?.data?.message || error.message || 'Something went wrong. Please try again.';
            toast.error(errorMessage);
        } finally {
            setSubmitting(false);
        }
    };

    const renderPagination = () => {
        if (assets.length === 0 || pagination.totalPages <= 0) return null;

        return (
            <div className="employee-pagination">
                <div
                    className="pagination-limit-container"
                    style={{ display: 'flex', alignItems: 'center', gap: '10px', marginRight: 'auto' }}
                >
                    <label htmlFor="handoverPaginationLimit" style={{ margin: 0, fontSize: '14px', color: '#495057' }}>
                        Show:
                    </label>
                    <select
                        id="handoverPaginationLimit"
                        value={pagination.limit}
                        onChange={handleLimitChange}
                        disabled={submitting}
                        style={{
                            padding: '6px 10px',
                            fontSize: '14px',
                            border: '1px solid #ced4da',
                            borderRadius: '4px',
                            backgroundColor: 'white',
                            cursor: 'pointer',
                            minWidth: '70px'
                        }}
                    >
                        <option value="10">10</option>
                        <option value="25">25</option>
                        <option value="50">50</option>
                        <option value="100">100</option>
                    </select>
                </div>
                <div style={{ display: 'flex', gap: '5px' }}>
                    <button
                        type="button"
                        className="pagination-btn"
                        onClick={() => handlePageChange(1)}
                        disabled={pagination.currentPage === 1 || submitting}
                    >
                        «
                    </button>
                    <button
                        type="button"
                        className="pagination-btn"
                        onClick={() => handlePageChange(pagination.currentPage - 1)}
                        disabled={pagination.currentPage === 1 || submitting}
                    >
                        ‹
                    </button>
                    {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map((page) => (
                        <button
                            key={page}
                            type="button"
                            className={`pagination-btn ${pagination.currentPage === page ? 'active' : ''}`}
                            onClick={() => handlePageChange(page)}
                            disabled={submitting}
                        >
                            {page}
                        </button>
                    ))}
                    <button
                        type="button"
                        className="pagination-btn"
                        onClick={() => handlePageChange(pagination.currentPage + 1)}
                        disabled={pagination.currentPage === pagination.totalPages || submitting}
                    >
                        ›
                    </button>
                    <button
                        type="button"
                        className="pagination-btn"
                        onClick={() => handlePageChange(pagination.totalPages)}
                        disabled={pagination.currentPage === pagination.totalPages || submitting}
                    >
                        »
                    </button>
                </div>
            </div>
        );
    };

    return (
        <div className="employee-form">
            <section className="content">
                <div className="container-fluid">
                    <div className="row mb-3 employee-header-row">
                        <div className="col-md-8">
                            <h1 className="employee-title">
                                Assets Handover
                                <i className="fas fa-info-circle ml-2 employee-title-icon"></i>
                            </h1>
                            <p className="employee-description">
                                Select assets to hand over for {employeeName} ({employeeCode}) during resignation.
                            </p>
                        </div>
                        <div className="col-md-4 employee-view-header-actions">
                            <button
                                type="button"
                                className="employee-back-btn"
                                onClick={() => navigate('/application-status')}
                                disabled={submitting}
                            >
                                Back
                            </button>
                        </div>
                    </div>

                    <div className="card employee-content-card">
                        <div className="card-body">
                            {loading ? (
                                <Loading size={120} />
                            ) : (
                                <>
                                    {selectedAssetIds.length > 0 && (
                                        <p className="section-subtitle" style={{ marginBottom: '12px' }}>
                                            {selectedAssetIds.length} asset(s) selected
                                        </p>
                                    )}
                                    <div className="employee-table-wrapper">
                                        <table className="employee-table">
                                            <thead>
                                                <tr>
                                                    <th style={{ width: '48px' }}>
                                                        <input
                                                            type="checkbox"
                                                            checked={allCurrentPageSelected}
                                                            onChange={handleSelectAll}
                                                            disabled={assets.length === 0 || submitting}
                                                            aria-label="Select all assets on this page"
                                                        />
                                                    </th>
                                                    <th>SI No.</th>
                                                    <th>Asset Name</th>
                                                    <th>Brand</th>
                                                    <th>Description</th>
                                                    <th>Purchase Date</th>
                                                    <th>Warranty Expiry</th>
                                                    <th>Status</th>
                                                    <th>Allocation Status</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {assets.length === 0 ? (
                                                    <tr>
                                                        <td colSpan="9" className="employee-empty">
                                                            No assets found for this employee
                                                        </td>
                                                    </tr>
                                                ) : (
                                                    assets.map((asset, index) => {
                                                        const status = getAssetStatus(asset);
                                                        const allocationStatus =
                                                            asset.allocation?.[0]?.status || '-';
                                                        return (
                                                            <tr key={asset.id}>
                                                                <td>
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={isAssetSelected(asset.id)}
                                                                        onChange={() => handleToggleAsset(asset.id)}
                                                                        disabled={submitting}
                                                                        aria-label={`Select ${getAssetName(asset)}`}
                                                                    />
                                                                </td>
                                                                <td>{getSerialNumber(index)}</td>
                                                                <td>{getAssetName(asset)}</td>
                                                                <td>{asset.brand || '-'}</td>
                                                                <td>{getDescription(asset)}</td>
                                                                <td>{formatDate(asset.purchaseDate)}</td>
                                                                <td>{formatDate(asset.warrantyExpiry)}</td>
                                                                <td>
                                                                    <span className={`employee-status employee-status-${getStatusClass(status)}`}>
                                                                        {status}
                                                                    </span>
                                                                </td>
                                                                <td>{allocationStatus}</td>
                                                            </tr>
                                                        );
                                                    })
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                    {renderPagination()}
                                    <div className="form-actions" style={{ marginTop: '20px' }}>
                                        <button
                                            type="button"
                                            className="btn-secondary"
                                            onClick={() => navigate('/application-status')}
                                            disabled={submitting}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="button"
                                            className="btn-primary"
                                            onClick={handleSubmitHandover}
                                            disabled={submitting || selectedAssetIds.length === 0}
                                        >
                                            {submitting ? 'Submitting...' : 'Submit Handover'}
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default AssetsHandover;
